import React, { memo, useEffect, useMemo, useCallback, useContext } from 'react';
import {
  PRESCRIBER, PATIENT, INFO_CARD_THEME,
  INTAKE, ADD_PRODUCT_INFO, PRODUCT_INFO,
} from '../../constants/patientChart.constant';
import { PatientChartContext } from '../../contexts/patientChart';
import { PatientChartControllerHOC } from '../../controllers/patientChart';
import {
  StorefrontHistory, ExtendedConsult,
  OrderHeading, Products,
  PatientInformation, InfoCard, TabDetailsV2 as TabDetails, TabsWrapperV2 as TabsWrapper,
} from '../../components/patientChart';
import { PageHeading, useAvatarTitle } from '../../components/PageHeading';
import { Order } from '../../components/subComponents/table';
import { getLoggedInUser } from '../../utils/jose';
import { generateEmailTemplateList } from '../../utils/emailTemplates';
import { FEATURE } from '../../utils/feature';
import { flattenWithoutComma } from '../../utils/flatten';
import { sortedTextArray } from '../../utils/sortBy';
import Logo from '../../assets/logo.png';
import '../patientChart/styles.css';

const hasFeature = FEATURE();

export const PatientHistory = memo(({
  match,
  // eslint-disable-next-line no-unused-vars
  history,
  orders = {},
  productInfo = PRODUCT_INFO(),
  addProductInfo = ADD_PRODUCT_INFO(),
  selectedPrescriberInfo: prescriber = PRESCRIBER(),
  selectedPatientInfo: patient = PATIENT(),
  patientUploads = {},
  changeProducts = {},
  emailSubjects = {},
  emailReasons = {},
  callSummary = {},
  callPatient: callPatientInfo = {},
  textPatient: sendTextInfo = {},
  emailPatient: sendEmailInfo = {},
  callPatientDetails: callingInfo = {},
  textmessageTemplateData: messageTemplates = [],
  emailTemplate: emailTemplates = [],
  emailTemplatesV2 = {},
  consultQuestion: intake = INTAKE(),
  productCost = {},
  saveExtendedConsult: saveExtendedConsultInfo = {},
  emailMessages = {},
  orderNotes = {},
  patientNotes = {},
  medicalOrderNotes = {},
  medicalPatientNotes = {},
  orderNotesAPI = {},
  patientNotesAPI = {},
  medicalOrderNotesAPI = {},
  medicalPatientNotesAPI = {},
  historyAPI = {},
  historyData = [],
  tasksInfo = {},
  icd10CodesByPatientId = {},

  GetTasks: getTasks = async () => {},
  GetScheduleVisitsLV: getScheduledVisits = async () => {},
  GetPendingFilesLV: getAsyncOrders = async () => {},
  GetNotes: getNotes = async () => {},
  GetOrderNotes: getOrderNotes = async (requestBody = {}) => {},
  GetPatientNotes: getPatientNotes = async (requestBody = {}) => {},
  GetMedicalPatientNotes: getMedicalPatientNotes = async (requestBody = {}) => {},
  GetMedicalOrderNotes: getMedicalOrderNotes = async (requestBody = {}) => {},
  GetPatientInfo: getPatient = async () => {},
  GetConsultQuestions: getConsultQuestions = async () => {},
  ResetProductInfoV2: resetProductInfo = async () => {},
  GetSoapNoteInfo: getSoapNote = async () => {},
  GetHeaderInfo: getPrescriber = async () => {},
  GetPatientUploads: getUploads = async () => {},
  GetTextPatientTemplates: getMessageTemplates = async () => {},
  GetEmailReasons: getEmailReasons = async () => {},
  GetEmail: getEmails = async (requestBody = {}) => {},
  GetDenialReasons: getDenialReasons = async () => {},
  GetSubjectAndMailForAllTemplates: getSubjects = async () => {},
  GetCallPatientDetails: getCallingInfo = async () => {},
  GetSendOrderToCSReasons: getSendToPSReasons = async () => {},
  OrderCallSummaryReasons: getCallSummaries = async () => {},
  InsertCallSummary: saveCallSummary = async () => {},
  CallToPatientPhys: callPatient = async () => {},
  TextPatient: sendText = async () => {},
  EmailPatientDetails: sendEmail = async () => {},
  GetProductCost: getProductCost = async () => {},
  ResetProductCost: resetProductCost = async () => {},
  AddProduct: addProduct = async () => {},
  GetSavedFlags: getSavedFlags = async () => {},
  ResetOrderAndPrescriberFlags: resetFlags = async () => {},
  ResetSavedFlags: resetSavedFlags = async () => {},
  SaveExtendedConsult: saveExtendedConsult = async (requestBody = []) => {},
  SetAddSoapNoteValue: setSoapNoteAdded = async (requestBody = {}) => {},
  ResetApproveAndEmailPrepInfo: resetApprovalInfo = async () => {},
  GetHistory: getHistory = async (requestBody = { OrderId: '' }) => {},
  ResetHistoryTab: resetHistory = async () => {},
  GetICD10CodesByPatientId: getICD10CodesByUId = async () => ({}),
}) => {
  const {
    OrderDate: orderDate = '',
    api: productInfoAPI = {},
    productSet = new Set(),
  } = productInfo;
  const { orderId: OrderId, patientId: UserId } = match.params;
  const DoctorId = useMemo(() => getLoggedInUser().UserId, []);

  const {
    tab,
    setTab,
    visitType,
    setExtendedConsult,
    setStorefrontHistory,
    handleLoadProductsListing,
  } = useContext(PatientChartContext);

  const { avatarTitle } = useAvatarTitle(prescriber);

  const handlePatientResponse = async ({ data }) => {
    const patient = data.Data;
    const {
      ICD10Codes: _icd10Codes = ['NONE'],
      StorefrontMedications: _storefrontHistory = ['NONE'],
      PersonalMedicatoins: _medications = ['NONE'],
      SeriousIllness: _illnesses = ['NONE'],
      Allergies: _allergies = ['NONE'],
    } = patient;

    const storefrontHistoryArray = flattenWithoutComma(_storefrontHistory || ['NONE']);
    const icd10CodesArray = flattenWithoutComma(_icd10Codes || ['NONE']);
    const medicationsArray = flattenWithoutComma(_medications || ['NONE']);
    const illnessesArray = flattenWithoutComma(_illnesses || ['NONE']);
    const allergiesArray = flattenWithoutComma(_allergies || ['NONE']);

    const sortedStorefrontHistoryArray = sortedTextArray(storefrontHistoryArray, Order.asc);

    const storefrontHistory = sortedStorefrontHistoryArray.join(', ');
    const icd10Codes = icd10CodesArray.join(', ');
    const medications = medicationsArray.join(', ');
    const illnesses = illnessesArray.join(', ');
    const allergies = allergiesArray.join(', ');

    const _extendedConsult = { medications, illnesses, allergies };

    setExtendedConsult({ prev: { ..._extendedConsult }, current: { ..._extendedConsult } });
    setStorefrontHistory({ medications: storefrontHistory, icd10Codes });
  };

  useEffect(() => {
    if (!prescriber.FirstName) {
      getPrescriber();
    }

    getTasks();
    handleLoadProductsListing({ OrderId });
    getPatient({ UserId, OrderId }).then(handlePatientResponse);
    getNotes({ OrderId });
    getSoapNote({ OrderId });
    getOrderNotes({ OrderId });
    getPatientNotes({ UserId });
    getMedicalOrderNotes({ OrderId });
    getMedicalPatientNotes({ OrderId });
    getConsultQuestions({ OrderId });
    getICD10CodesByUId({ UserId });

    return () => {
      resetProductInfo();
      resetHistory();
    };
  }, []);

  const _callSummaryReasons = useMemo(() => {
    const reasons = callSummary.reasons || [];

    return reasons.map(({ AltItem: id, Description: title }) => ({
      title,
      label: title,
      value: id,
      id
    }));
  }, [callSummary.reasons]);

  const _messageTemplates = useMemo(() => {
    const templates = messageTemplates || [];

    return {
      data: templates.map(({ TemplateId: id, TemplateText: display, TemplateTitle: title }) => ({
        title,
        label: title,
        display,
        value: id,
        id
      })),
    };
  }, [messageTemplates]);

  const _emailTemplates = useMemo(() => {
    const templates = emailTemplates || [];

    return {
      data: templates.map(({ EmailMessageId: id, EmailTemplateTitle: title }) => ({
        title,
        label: title,
        value: id,
        id
      })),
      subjects: emailSubjects.api,
    };
  }, [emailTemplates, emailSubjects]);

  const _emailTemplatesV2 = useMemo(() => {
    const { templateCollection, api = {} } = emailTemplatesV2;
    return {
      data: generateEmailTemplateList(emailTemplatesV2, productSet),
      subjects: {
        ...api,
        data: templateCollection,
      },
    };
  }, [emailTemplatesV2, productSet]);

  const _emailReasons = useMemo(() => {
    return { ...emailReasons.api, valuesCollection: emailReasons.valuesCollection };
  }, [emailReasons]);

  const handleTabClick = useCallback((tab) => {
    setTab(tab);
  }, []);

  const PageHeadingComponent = new Map([
    [true, <PageHeading logo={Logo} message={prescriber.Message} avatarTitle={avatarTitle} />],
    [false, <></>]
  ]);
  const OrderHeadingComponent = new Map([
    [true, <OrderHeading orderId={OrderId} orderDate={orderDate} visitType={visitType} scheduledVisit={patient.StartDateTime_UTC} loading={productInfoAPI.loading || patient.api.loading} />],
    [false, <></>]
  ]);
  const PatientInformationComponent = new Map([
    [true, (
      <InfoCard
        heading='Patient Information'
        content={
          <PatientInformation
            orderId={OrderId}
            patientId={UserId}
            doctorId={DoctorId}
            patient={patient}
            product={productInfo}
            calling={callingInfo}
            callSummaryReasons={_callSummaryReasons}
            messageTemplates={_messageTemplates}
            emailTemplates={_emailTemplates}
            emailTemplatesV2={_emailTemplatesV2}
            emailReasons={_emailReasons}
            callPatient={callPatient}
            saveCallSummary={saveCallSummary}
            callPatientInfo={callPatientInfo}
            sendText={sendText}
            sendTextInfo={sendTextInfo}
            sendEmail={sendEmail}
            sendEmailInfo={sendEmailInfo}
            getEmails={getEmails}
          />
        }
        bottomGutter
      />
    )],
    [false, <></>]
  ]);
  const ProductsComponent = new Map([
    [true, (
      <Products
        data={{
          productCost,
          productInfo,
          addProductInfo,
          changeProducts,
        }}
        action={{
          getProductCost,
          resetProductCost,
          addProduct,
        }}
      />
    )],
    [false, <></>]
  ]);
  const TabWrapperComponent = new Map([
    [true, <TabsWrapper tab={tab} onClick={handleTabClick} />],
    [false, <></>]
  ]);
  const TabDetailsComponent = new Map([
    [true, (
      <TabDetails
        tab={tab}
        data={{
          orderId: OrderId,
          patientId: UserId,
          doctorId: DoctorId,
          intake,
          patientUploads,
          emailMessages,
          notes: {
            order: orderNotes,
            patient: patientNotes,
            medical: {
              order: medicalOrderNotes,
              patient: medicalPatientNotes,
            },
          },
          notesAPI: {
            order: orderNotesAPI,
            patient: patientNotesAPI,
            medical: {
              order: medicalOrderNotesAPI,
              patient: medicalPatientNotesAPI,
            },
          },
          notesToDisplay: [
            { key: 'mo', },
            { key: 'o', },
          ],
          history: {
            ...historyAPI,
            data: historyData || [],
          },
          tasksInfo,
        }}
      />
    )],
    [false, <></>]
  ]);
  const ExtendedConsultComponent = new Map([
    [true, (
      <InfoCard
        heading='Extended Consult'
        content={
          <ExtendedConsult
            orderId={Number(OrderId)}
            patientId={Number(UserId)}
            doctorId={Number(DoctorId)}
            patient={patient}
            product={productInfo}
            orders={orders}
            intake={intake}
            saveExtendedConsult={saveExtendedConsult}
            saveExtendedConsultInfo={saveExtendedConsultInfo}
          />
        }
        bottomGutter
        theme={INFO_CARD_THEME.INFO}
      />
    )],
    [false, <></>]
  ]);
  const StorefrontHistoryComponent = new Map([
    [true, (
      <InfoCard
        heading='Storefront History'
        content={
          <StorefrontHistory
            orderId={Number(OrderId)}
            patientId={Number(UserId)}
            doctorId={Number(DoctorId)}
            patient={patient}
            product={productInfo}
            orders={orders}
            icd10Codes={icd10CodesByPatientId}
          />
        }
        theme={INFO_CARD_THEME.INFO}
      />
    )],
    [false, <></>]
  ]);

  return (
    <div className='patient-chart-container flex flex-column flex-fill'>
      {PageHeadingComponent.get(hasFeature.PAGE_HEADING)}

      <div className='patient-chart-content flex p-l-6 p-r-6'>
        <div className='patient-chart-info-cards flex flex-column gap-24 flex-fill'>
          <div className='flex gap-24 patient-chart-info-section-a'>
            <div className='flex flex-column gap-24 flex-fill'>
              {OrderHeadingComponent.get(hasFeature.ORDER_HEADING)}
            </div>
          </div>

          <div className='flex gap-12 patient-chart-info-section-b'>
            <div className='content-box min-width-280 max-width-280 p-r-3 scroll-y-auto patient-chart-info-section-b-patient-info-column-a'>
              {PatientInformationComponent.get(hasFeature.PATIENT_INFORMATION)}
              {ExtendedConsultComponent.get(hasFeature.EXTENDED_CONSULT)}
              {StorefrontHistoryComponent.get(hasFeature.STOREFRONT_HISTORY)}
            </div>

            <div className='flex flex-column gap-24 flex-fill'>
              <div className='flex flex-column patient-chart-info-section-b-products-wrapper'>
                <div className='patient-chart-info-section-b-products'>
                  {ProductsComponent.get(hasFeature.PRODUCTS)}
                </div>
              </div>

              <div className='flex gap-24'>
                <div className='flex flex-column gap-12 flex-fill'>
                  {TabWrapperComponent.get(hasFeature.TABS)}
                  {TabDetailsComponent.get(hasFeature.TABS)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PatientChartControllerHOC(PatientHistory);
